import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { Button, Card, CardBody, CardHeader, Switch } from "@heroui/react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { PhoneInput } from "@/components/ui/phone-input";
import type { Customer, BusinessAccount } from "@/api.types";
import { useState } from "react";
import {
  User,
  Building2,
  Mail,
  Phone,
  AlertCircle,
  LogOut,
} from "lucide-react";
import { logger } from "@/services/logger";
import { HeroFormField, HeroInputField } from "@/components/hero-form";

const createProfileSchema = (t: (key: string) => string) =>
  z.object({
    firstName: z.string().min(1, t("validation.firstNameRequired")),
    lastName: z.string().min(1, t("validation.lastNameRequired")),
    email: z.string().email(t("validation.emailInvalid")),
    phone: z
      .string()
      .refine(
        (value) => value.startsWith("+1") && isValidPhoneNumber(value, "CA"),
        { message: t("validation.phoneInvalid") }
      ),
  });

const createBusinessAccountSchema = (t: (key: string) => string) =>
  z.object({
    companyName: z.string().min(1, t("validation.companyNameRequired")),
    taxNumber: z.string().optional(),
  });

type ProfileFormValues = z.infer<ReturnType<typeof createProfileSchema>>;
type BusinessAccountFormValues = z.infer<
  ReturnType<typeof createBusinessAccountSchema>
>;

export default function OnboardingStep1Screen() {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const { token, customer, logout } = useSession();
  const queryClient = useQueryClient();
  const [isBusinessAccount, setIsBusinessAccount] = useState(false);
  const profileSchema = createProfileSchema(t);
  const businessAccountSchema = createBusinessAccountSchema(t);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: customer?.firstName?.includes("@")
        ? ""
        : (customer?.firstName ?? ""),
      lastName: customer?.lastName?.includes("@")
        ? ""
        : (customer?.lastName ?? ""),
      email: customer?.email ?? "",
      phone: customer?.phone ? `${customer?.phone}` : "",
    },
  });

  const businessForm = useForm<BusinessAccountFormValues>({
    resolver: zodResolver(businessAccountSchema),
    defaultValues: {
      companyName: "",
      taxNumber: "",
    },
  });

  const initializeCustomerMutation = useMutation({
    mutationFn: async (data: {
      customerId: string;
      profile: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
      };
      businessAccount?: {
        isBusinessAccount: boolean;
        companyName: string;
        taxNumber?: string;
      };
    }) => {
      const response = await axios.post<{
        customer: Customer;
        businessAccount: BusinessAccount;
        customerId?: string;
      }>(`${import.meta.env.VITE_API_URL}/api/v2/customers/initialize`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer", "me"] });
      await navigate("/onboarding/success");
    },
    onError: (error) => {
      logger.error("Error initializing customer:", error);
    },
  });

  const onSubmit = async () => {
    const profileValid = await form.trigger();
    const businessValid = isBusinessAccount && (await businessForm.trigger());
    const isValid = profileValid && (!isBusinessAccount || businessValid);
    if (!isValid) return;

    const profileValues = form.getValues();
    const businessValues = businessForm.getValues();

    initializeCustomerMutation.mutate({
      customerId: customer?.id ?? "",
      profile: profileValues,
      businessAccount: isBusinessAccount
        ? {
            isBusinessAccount: true,
            companyName: businessValues.companyName,
            taxNumber: businessValues.taxNumber,
          }
        : undefined,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-default-50 to-background">
      <div className="max-w-2xl mx-auto p-6 md:p-10 space-y-10">
        {/* Editorial header · direction D */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("onboarding.welcome")}
            </div>
            <Button
              type="button"
              variant="light"
              size="sm"
              startContent={<LogOut className="h-4 w-4" aria-hidden="true" />}
              onPress={logout}
            >
              {t("accountMenu.signOut")}
            </Button>
          </div>
          <h1 className="text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
            {t("onboarding.titleLine1")}
            <br />
            <em className="text-primary">{t("onboarding.titleEmphasis")}</em>
          </h1>
          <p className="text-base text-default-600">{t("onboarding.lead")}</p>
        </div>

        <div className="space-y-6">
          {/* Personal Information Card */}
          <Card shadow="sm">
            <CardHeader className="flex flex-col items-start gap-1 pb-3">
              <div className="flex items-center gap-2 text-medium font-semibold">
                <User className="h-5 w-5" />
                {t("onboarding.personalTitle")}
              </div>
              <p className="text-small text-default-500">
                {t("onboarding.personalDescription")}
              </p>
            </CardHeader>
            <CardBody className="gap-4 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <HeroInputField
                  control={form.control}
                  name="firstName"
                  label={t("onboarding.firstName")}
                  placeholder={t("onboarding.firstNamePlaceholder")}
                  variant="flat"
                />
                <HeroInputField
                  control={form.control}
                  name="lastName"
                  label={t("onboarding.lastName")}
                  placeholder={t("onboarding.lastNamePlaceholder")}
                  variant="flat"
                />
                <div className="sm:col-span-2">
                  <HeroInputField
                    control={form.control}
                    name="email"
                    type="email"
                    label={t("onboarding.email")}
                    placeholder={t("onboarding.emailPlaceholder")}
                    startContent={<Mail className="h-4 w-4 text-default-400" />}
                    variant="flat"
                  />
                </div>
                <div className="sm:col-span-2">
                  <HeroFormField
                    control={form.control}
                    name="phone"
                    render={({ field, fieldState }) => (
                      <div>
                        <label className="block text-tiny font-medium text-default-700 mb-1.5">
                          <span className="inline-flex items-center gap-1.5">
                            <Phone className="h-4 w-4" />
                            {t("onboarding.phone")}
                          </span>
                        </label>
                        <PhoneInput
                          placeholder={t("onboarding.phonePlaceholder")}
                          value={field.value}
                          onChange={(v) => field.onChange(v ?? "")}
                          onBlur={field.onBlur}
                          defaultCountry="CA"
                          countries={["CA"]}
                        />
                        {fieldState.error?.message && (
                          <p className="mt-1 text-tiny text-danger">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Business Account Card */}
          <Card shadow="sm">
            <CardHeader className="flex flex-col items-start gap-1 pb-3">
              <div className="flex items-center gap-2 text-medium font-semibold">
                <Building2 className="h-5 w-5" />
                {t("onboarding.businessTitle")}
              </div>
              <p className="text-small text-default-500">
                {t("onboarding.businessDescription")}
              </p>
            </CardHeader>
            <CardBody className="gap-4 pt-0">
              <div className="flex items-center justify-between rounded-lg border border-default-200 p-4">
                <div className="space-y-0.5">
                  <label className="text-medium font-medium">
                    {t("onboarding.businessToggle")}
                  </label>
                  <p className="text-small text-default-500">
                    {t("onboarding.businessToggleDescription")}
                  </p>
                </div>
                <Switch
                  isSelected={isBusinessAccount}
                  onValueChange={setIsBusinessAccount}
                />
              </div>

              {isBusinessAccount && (
                <div className="space-y-4 animate-in fade-in-50 slide-in-from-top-2">
                  <div className="flex gap-3 rounded-lg border border-warning/20 bg-warning/10 p-3">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-warning" />
                    <p className="text-small text-default-700">
                      {t("onboarding.businessLockedWarning")}
                    </p>
                  </div>

                  <HeroInputField
                    control={businessForm.control}
                    name="companyName"
                    label={t("onboarding.companyName")}
                    placeholder={t("onboarding.companyNamePlaceholder")}
                    variant="flat"
                  />
                  <HeroInputField
                    control={businessForm.control}
                    name="taxNumber"
                    label={t("onboarding.taxNumber")}
                    placeholder={t("onboarding.taxNumberPlaceholder")}
                    description={t("onboarding.taxNumberDescription")}
                    variant="flat"
                  />
                </div>
              )}
            </CardBody>
          </Card>

          {initializeCustomerMutation.isError && (
            <p role="alert" className="text-small text-danger">
              {t("onboarding.setupFailed")}
            </p>
          )}

          {/* Submit Button */}
          <Button
            onPress={onSubmit}
            isDisabled={initializeCustomerMutation.isPending}
            isLoading={initializeCustomerMutation.isPending}
            color="primary"
            size="lg"
            fullWidth
            className="text-medium font-semibold h-13"
          >
            {initializeCustomerMutation.isPending
              ? t("onboarding.creatingAccount")
              : t("onboarding.completeSetup")}
          </Button>
        </div>
      </div>
    </div>
  );
}
